/* eslint-disable no-unused-vars */

import React, { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { iconMap } from "./Icons.jsx";

const HeaderLink = forwardRef(
  (
    {
      children,
      href,
      onClick,
      className = "",
      hasDropdown = false,
      isActive = false,
      isOpen = false,
      icon,
      iconPosition = "right",
      size = "medium",
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const renderIcon = (iconName) => {
      if (!iconName) return null;
      const Icon = iconMap?.[iconName];
      if (!Icon) return null;
      return <Icon className={currentSize.iconSize} />;
    };

    const sizes = {
      small: {
        text: "text-sm",
        padding: "pb-2 sm:pb-3",
        gap: "gap-1",
        iconSize: "w-3 h-3 sm:w-4 sm:h-4",
      },
      medium: {
        text: "text-sm sm:text-sm md:text-sm lg:text-sm",
        padding: "pb-3 sm:pb-4",
        gap: "gap-1 sm:gap-1.5",
        iconSize: "w-4 h-4 sm:w-5 sm:h-5",
      },
      large: {
        text: "text-base sm:text-lg",
        padding: "pb-4 sm:pb-5",
        gap: "gap-1.5 sm:gap-2",
        iconSize: "w-5 h-5 sm:w-6 sm:h-6",
      },
    };

    const currentSize = sizes[size] || sizes["medium"];

    const DropdownIcon = ({ isDropdownOpen }) => (
      <motion.svg
        className={currentSize.iconSize}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        animate={{ rotate: isDropdownOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"
        />
      </motion.svg>
    );

    const ExternalIcon = () => (
      <svg
        className={currentSize.iconSize}
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M18 19H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h5c.55 0 1-.45 1-1s-.45-1-1-1H5c-1.11 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55-.45 1-1 1M14 4c0 .55.45 1 1 1h2.59l-9.13 9.13c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L19 6.41V9c0 .55.45 1 1 1s1-.45 1-1V4c0-.55-.45-1-1-1h-5c-.55 0-1 .45-1 1" />
      </svg>
    );

    const baseClasses = `
      relative inline-flex items-center
      font-sweet uppercase
      cursor-pointer
      transition-colors duration-200
      ${currentSize.text}
      ${currentSize.padding}
      ${currentSize.gap}
      ${isHovered || isActive ? "text-[#87d4f6]" : "text-white"}
      ${className}
    `;

    const UnderlineBar = () => (
      <motion.div
        className="absolute bottom-0 left-0 bg-[#87d4f6] h-0.5 sm:h-1"
        initial={{ width: "0%" }}
        animate={{ width: isHovered || isActive ? "100%" : "0%" }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      />
    );

    const LinkContent = () => (
      <>
        <span className={`flex items-center ${currentSize.gap}`}>
          {icon && iconPosition === "left" && (
            <span className="flex-shrink-0">
              {icon === "external" ? (
                <ExternalIcon />
              ) : icon === "dropdown" ? (
                <DropdownIcon isDropdownOpen={isOpen} />
              ) : React.isValidElement(icon) ? (
                React.cloneElement(icon, {
                  className: `${currentSize.iconSize} ${
                    icon.props?.className || ""
                  }`,
                })
              ) : (
                renderIcon(icon) || icon
              )}
            </span>
          )}

          <span className="whitespace-nowrap">{children}</span>

          {icon && iconPosition === "right" && (
            <span className="flex-shrink-0">
              {icon === "external" ? (
                <ExternalIcon />
              ) : icon === "dropdown" ? (
                <DropdownIcon isDropdownOpen={isOpen} />
              ) : React.isValidElement(icon) ? (
                React.cloneElement(icon, {
                  className: `${currentSize.iconSize} ${
                    icon.props?.className || ""
                  }`,
                })
              ) : (
                renderIcon(icon) || icon
              )}
            </span>
          )}

          {hasDropdown && !icon && (
            <span className="flex-shrink-0">
              <DropdownIcon isDropdownOpen={isOpen} />
            </span>
          )}
        </span>

        <UnderlineBar />
      </>
    );

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const handleTouchStart = () => setIsHovered(true);
    const handleTouchEnd = () => {
      setTimeout(() => setIsHovered(false), 150);
    };

    if (href) {
      const isExternal = href.startsWith("http") || href.startsWith("//");

      if (isExternal) {
        return (
          <a
            ref={ref}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={baseClasses}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            {...props}
          >
            <LinkContent />
          </a>
        );
      }

      return (
        <Link
          ref={ref}
          to={href}
          className={baseClasses}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          {...props}
        >
          <LinkContent />
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        className={baseClasses}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        {...props}
      >
        <LinkContent />
      </button>
    );
  }
);

HeaderLink.displayName = "HeaderLink";

export { HeaderLink };
export default HeaderLink;
